// Rowan University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Rowan's Common Data Set 2025-2026 (rowan_university_cds.pdf), the file
//    Institutional Data & Analytics links from its Institutional Research
//    page: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 (no early plans).
//    Its section G is not used: Rowan checked the box that 2026-2027 costs
//    were not yet available and filled G with 2025-2026 figures.
//  - Rowan Financial Aid, Cost of Attendance 2026-2027 (full-time
//    undergraduate, in-state and out-of-state). Its items sum to its totals
//    ($42,666 and $54,630). The page does not say whether its housing
//    figure is for on-campus or off-campus housing.
//  - admissions.rowan.edu: Application Calendar & Fees (first-year deadlines,
//    nursing and accelerated medical and PA programs), Test Optional
//    Admissions, and Art, Music, Theatre & Dance. The calendar's dates carry
//    no year; the testing page's date list labels March 1 "Fall 2027" but
//    still labels the January 31 scholarship date "for Fall 2026". Rowan
//    publishes no decision dates.
//  - College Scorecard, school id 184782 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Rowan Common Data Set 2025-2026',
        url: 'https://sites.rowan.edu/academic-affairs/ida/institutional-research/',
        read: '2026-10-01',
    },
    calendar: {
        label: 'Rowan Admissions, Application Calendar & Fees',
        url: 'https://admissions.rowan.edu/admissions-process/app-calendar-fees.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Rowan Admissions, Test Optional Admissions',
        url: 'https://admissions.rowan.edu/admissions-process/freshman-requirements/standardized-scores.html',
        read: '2026-10-01',
    },
    arts: {
        label: 'Rowan Admissions, Art, Music, Theatre & Dance',
        url: 'https://admissions.rowan.edu/admissions-process/art-music-td.html',
        read: '2026-10-01',
    },
    coa: {
        label: 'Rowan Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://sites.rowan.edu/financial-aid/cost-of-attendance/annual-coa-2627.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?184782-Rowan-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rowan-university',
    kbId: 'rowan_university',
    name: 'Rowan University',
    shortName: 'Rowan',
    location: 'Glassboro, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Rowan graphic standards, university
    // colors); decoration only.
    branding: {
        primary: '#57150B',
        secondary: '#FFCC00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rowan_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22410,
        admitted: 17152,
        enrolled: 3455,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 17318, admitted: 13517 },
            { group: 'Out-of-state', applied: 2831, admitted: 2022 },
            { group: 'International', applied: 2261, admitted: 1613 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rowan\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Rowan has no early decision or early action plan; the Common Data Set answers no to both. January 31 is a priority date for merit scholarships, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some programs have earlier deadlines or ask for more:',
        rows: [
            { school: 'Nursing (BSN)', requirement: 'Apply by December 1 and send all required materials by December 15.', source: 'calendar' },
            { school: '3+4 BS/MD, 3+4 BS/DO and 3+2 BS/PA accelerated programs', requirement: 'Apply by November 1. These programs are open only to first-time, full-time first-year students. Applicants must send SAT or ACT scores; see Testing.', source: 'calendar' },
            { school: 'Art', requirement: 'Submit a portfolio online through SlideRoom; you can also ask for a one-on-one, in-person portfolio review.', source: 'arts' },
            { school: 'Music', requirement: 'Schedule an audition with the Department of Music through the Applicant Portal. Music Industry applicants send a portfolio and essay through the portal instead.', source: 'arts' },
            { school: 'Theatre and dance', requirement: 'Schedule an audition with the Department of Theatre & Dance through the Applicant Portal.', source: 'arts' },
        ],
        source: 'calendar',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'March 1' },
        rows: [
            { plan: 'Priority (merit scholarships)', deadline: 'January 31', decision: null, binding: false },
            { plan: 'Fall application deadline', deadline: 'March 1', decision: null, binding: false },
        ],
        note: 'Apply by January 31 to be considered for a merit scholarship award. The application fee is $65, and the $200 enrollment confirmation is due August 14. Rowan does not publish decision dates.',
        source: 'calendar',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Rowan is test-optional: you may send SAT or ACT scores, and you are not penalized if you do not. Applicants to the 3+4 BS/MD or BS/DO programs or the 3+2 Physician Assistant program must send scores. Rowan superscores the SAT, using your best section scores across all test dates. If you enroll, you may have to take placement tests.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'calendar',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [18300, 29664] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1330, 1330] },
            { item: 'Housing', amounts: [11897, 11897] },
            { item: 'Food', amounts: [5693, 5693] },
            { item: 'Personal spending', amounts: [3080, 3080] },
            { item: 'Transportation', amounts: [2322, 2922] },
            { item: 'Federal loan fees', amounts: [44, 44] },
        ],
        totals: [42666, 54630],
        note: 'The financial aid office\'s estimate for a full-time undergraduate (12 to 17 semester hours), fall and spring. Your own costs depend on your program, housing and meal choices.',
        source: 'coa',
        netPrice: {
            average: 23938,
            byIncome: [
                { income: '$0 to $30,000', amount: 16231 },
                { income: '$30,001 to $48,000', amount: 16468 },
                { income: '$48,001 to $75,000', amount: 20820 },
                { income: '$75,001 to $110,000', amount: 28732 },
                { income: 'Over $110,000', amount: 32287 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rowan\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
